import React, { useState, useEffect, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';
import {
  DollarSign, BookOpen, FileText, TrendingUp, TrendingDown,
  Users, Building2, Calculator, Shield, Settings, BarChart3,
  Receipt, CreditCard, Wallet, PieChart, Activity, Lock,
  CheckCircle, XCircle, AlertCircle, ArrowRight, ArrowLeft,
  Calendar, Filter, Download, Search, Plus, Edit, Eye,
  RefreshCw, Zap, Link as LinkIcon, Database, FileCheck,
  ChevronDown, MoreHorizontal, Menu, X, ChevronLeft, Globe, Languages
} from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { LanguageProvider, useLanguage } from './context/LanguageContext';

// Import sub-components
import DashboardTab from './components/DashboardTab';
import GeneralLedgerTab from './components/GeneralLedgerTab';
import AccountsPayableTab from './components/AccountsPayableTab';
import AccountsReceivableTab from './components/AccountsReceivableTab';
import ReconciliationTab from './components/ReconciliationTab';
import TaxZATCATab from './components/TaxZATCATab';
import AssetManagementTab from './components/AssetManagementTab';
import PostingRulesTab from './components/PostingRulesTab';
import ClosingAuditTab from './components/ClosingAuditTab';
import SettingsTab from './components/SettingsTab';

const FinancialDashboardContent = () => {
  const location = useLocation();
  const { language, toggleLanguage, t } = useLanguage();
  const [activeTab, setActiveTab] = useState('dashboard');
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  // Initialize - always default to true (visible)
  const [showSideMenu, setShowSideMenu] = useState(true);
  const [showMoreDropdown, setShowMoreDropdown] = useState(false);
  const [showNewEntryModal, setShowNewEntryModal] = useState(false);
  const [showLanguageDropdown, setShowLanguageDropdown] = useState(false);
  const dropdownRef = useRef(null);
  const sideMenuRef = useRef(null);
  const languageDropdownRef = useRef(null);
  
  // Load state from localStorage on mount - default to true (visible)
  useEffect(() => {
    const saved = localStorage.getItem('financialSideMenuOpen');
    // Only set to false if explicitly saved as 'false', otherwise default to true
    if (saved === 'false') {
      setShowSideMenu(false);
    } else {
      // Always default to visible
      setShowSideMenu(true);
      if (saved !== 'true') {
        localStorage.setItem('financialSideMenuOpen', 'true');
      }
    }
  }, []); // Only run once on mount - never again
  
  // Persist side menu state in localStorage when it changes
  useEffect(() => {
    localStorage.setItem('financialSideMenuOpen', showSideMenu.toString());
  }, [showSideMenu]);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Handle click outside dropdown (only for dropdown, not side menu)
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setShowMoreDropdown(false);
      }
      if (languageDropdownRef.current && !languageDropdownRef.current.contains(event.target)) {
        setShowLanguageDropdown(false);
      }
      // Don't auto-hide side menu on desktop - only allow manual toggle
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Main tabs (visible in header)
  const mainTabs = [
    { id: 'dashboard', label: t('dashboard'), icon: BarChart3 },
    { id: 'general-ledger', label: t('generalLedger'), icon: BookOpen },
    { id: 'accounts-payable', label: t('accountsPayable'), icon: CreditCard },
    { id: 'accounts-receivable', label: t('accountsReceivable'), icon: Receipt },
    { id: 'reconciliation', label: t('reconciliation'), icon: FileCheck },
    { id: 'tax-zatca', label: t('taxZatca'), icon: Shield },
    { id: 'asset-management', label: t('assetManagement'), icon: Building2 },
  ];

  // Side menu items
  const sideMenuItems = [
    { id: 'posting-rules', label: t('postingRules'), icon: Zap },
    { id: 'closing-audit', label: t('closingAudit'), icon: Lock },
    { id: 'settings', label: t('settings'), icon: Settings },
  ];

  const quickStats = [
    {
      label: t('totalAssets'),
      value: 'SAR 12,450,000',
      change: '+5.2%',
      trend: 'up',
      icon: TrendingUp,
      color: 'text-green-600',
      bgColor: 'bg-green-50'
    },
    {
      label: language === 'ar' ? 'إجمالي الخصوم' : 'Total Liabilities',
      value: 'SAR 8,230,000',
      change: '+2.1%',
      trend: 'up',
      icon: TrendingDown,
      color: 'text-red-600',
      bgColor: 'bg-red-50'
    },
    {
      label: language === 'ar' ? 'صافي الدخل (هذا الشهر)' : 'Net Income (MTD)',
      value: 'SAR 1,245,000',
      change: '+12.5%',
      trend: 'up',
      icon: DollarSign,
      color: 'text-blue-600',
      bgColor: 'bg-blue-50'
    },
    {
      label: language === 'ar' ? 'الوضع النقدي' : 'Cash Position',
      value: 'SAR 3,890,000',
      change: '+8.3%',
      trend: 'up',
      icon: Wallet,
      color: 'text-emerald-600',
      bgColor: 'bg-emerald-50'
    },
    {
      label: language === 'ar' ? 'الذمم المدينة المتأخرة' : 'Aged Receivables',
      value: 'SAR 2,145,000',
      change: '-3.2%',
      trend: 'down',
      icon: Receipt,
      color: 'text-orange-600',
      bgColor: 'bg-orange-50'
    },
    {
      label: language === 'ar' ? 'الذمم الدائنة المتأخرة' : 'Aged Payables',
      value: 'SAR 1,890,000',
      change: '+1.5%',
      trend: 'up',
      icon: CreditCard,
      color: 'text-purple-600',
      bgColor: 'bg-purple-50'
    }
  ];

  return (
    <div className={language === 'ar' ? 'rtl' : 'ltr'} dir={language === 'ar' ? 'rtl' : 'ltr'}>
      {/* Fixed Left/Right Side Menu - Always Visible */}
      {/* Overlay for Mobile */}
      {showSideMenu && window.innerWidth < 1024 && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          onClick={() => setShowSideMenu(false)}
          className="fixed inset-0 bg-black/50 z-40 lg:hidden"
        />
      )}
      
      {/* Toggle Button - Show when menu is hidden */}
      {!showSideMenu && (
        <button
          onClick={() => setShowSideMenu(true)}
          className={`fixed top-20 z-50 p-2 bg-white shadow-lg border border-gray-200 hover:bg-gray-50 transition-colors ${
            language === 'ar' 
              ? 'right-0 rounded-l-lg border-l' 
              : 'left-0 rounded-r-lg border-r'
          }`}
          title={t('view')}
        >
          <Menu className="h-5 w-5 text-gray-600" />
        </button>
      )}

      {/* Side Menu - Fixed at left/right edge, below header */}
      <div
        ref={sideMenuRef}
        className={`fixed top-20 h-[calc(100vh-5rem)] w-56 bg-white shadow-lg z-50 transition-transform duration-300 ease-in-out ${
          language === 'ar' 
            ? `right-0 rounded-l-xl border-l border-gray-200 ${showSideMenu ? 'translate-x-0' : 'translate-x-full'}`
            : `left-0 rounded-r-xl border-r border-gray-200 ${showSideMenu ? 'translate-x-0' : '-translate-x-full'}`
        }`}
      >
        <div className={`p-4 h-full overflow-y-auto flex flex-col ${language === 'ar' ? 'rtl' : 'ltr'}`}>
          <div className={`flex items-center justify-between mb-4 pb-4 border-b border-gray-100 ${language === 'ar' ? 'flex-row-reverse' : ''}`}>
            <h2 className="text-sm font-semibold text-gray-700 uppercase tracking-wider">{t('more')}</h2>
            <button
              onClick={() => setShowSideMenu(false)}
              className="p-1.5 hover:bg-gray-100 rounded-md transition-colors"
              title={t('close')}
            >
              {language === 'ar' ? (
                <ChevronLeft className="h-4 w-4 text-gray-500 rotate-180" />
              ) : (
                <ChevronLeft className="h-4 w-4 text-gray-500" />
              )}
            </button>
          </div>
          
          {/* Language Toggle */}
          <div className="mb-4 pb-4 border-b border-gray-100">
            <div className="relative" ref={languageDropdownRef}>
              <button
                onClick={() => setShowLanguageDropdown(!showLanguageDropdown)}
                className={`w-full flex items-center justify-between gap-2 px-3 py-2 rounded-lg text-sm font-medium transition-all duration-200 ${
                  'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                }`}
              >
                <div className="flex items-center gap-2">
                  <Globe className="w-4 h-4 text-gray-400" />
                  <span>{t('language')}</span>
                </div>
                <ChevronDown className={`h-4 w-4 text-gray-400 transition-transform ${showLanguageDropdown ? 'rotate-180' : ''}`} />
              </button>
              <AnimatePresence>
                {showLanguageDropdown && (
                  <motion.div
                    initial={{ opacity: 0, y: -10 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -10 }}
                    className="absolute top-full left-0 right-0 mt-1 bg-white border border-gray-200 rounded-lg shadow-lg z-10 overflow-hidden"
                  >
                    <button
                      onClick={() => {
                        if (language !== 'en') {
                          toggleLanguage();
                        }
                        setShowLanguageDropdown(false);
                      }}
                      className={`w-full flex items-center gap-2 px-3 py-2 text-sm text-left hover:bg-gray-50 ${
                        language === 'en' ? 'bg-green-50 text-green-700' : 'text-gray-700'
                      }`}
                    >
                      <Languages className="w-4 h-4" />
                      <span>{t('english')}</span>
                      {language === 'en' && <CheckCircle className="w-4 h-4 ml-auto" />}
                    </button>
                    <button
                      onClick={() => {
                        if (language !== 'ar') {
                          toggleLanguage();
                        }
                        setShowLanguageDropdown(false);
                      }}
                      className={`w-full flex items-center gap-2 px-3 py-2 text-sm text-left hover:bg-gray-50 ${
                        language === 'ar' ? 'bg-green-50 text-green-700' : 'text-gray-700'
                      }`}
                    >
                      <Languages className="w-4 h-4" />
                      <span>{t('arabic')}</span>
                      {language === 'ar' && <CheckCircle className="w-4 h-4 ml-auto" />}
                    </button>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          </div>

          <div className="space-y-1">
            {sideMenuItems.map((item) => {
              const Icon = item.icon;
              const isActive = activeTab === item.id;
              return (
                <button
                  key={item.id}
                  onClick={() => {
                    setActiveTab(item.id);
                    // Only hide on mobile when clicking side menu items
                    if (window.innerWidth < 1024) {
                      setShowSideMenu(false);
                    }
                    // Keep side menu visible on desktop
                  }}
                  className={`w-full flex items-center gap-2.5 px-3 py-2 rounded-lg transition-all duration-200 ${
                    language === 'ar' ? 'flex-row-reverse text-right' : 'text-left'
                  } ${
                    isActive
                      ? 'bg-green-50 text-green-700 shadow-sm'
                      : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                  }`}
                >
                  <Icon className={`w-4 h-4 flex-shrink-0 ${isActive ? 'text-green-600' : 'text-gray-400'}`} />
                  <div className="flex-1 min-w-0">
                    <p className={`text-sm font-medium truncate ${isActive ? 'text-green-700' : 'text-gray-900'}`}>
                      {item.label}
                    </p>
                  </div>
                </button>
              );
            })}
          </div>
        </div>
      </div>

      {/* Main Content Area - Unaffected by sidebar */}
      <div className={`space-y-6 ${language === 'ar' ? 'rtl' : 'ltr'}`}>
        {/* Header */}
        <div className={`bg-gradient-to-r from-green-600 to-emerald-700 rounded-xl p-6 text-white ${language === 'ar' ? 'text-right' : 'text-left'}`}>
        <div className={`flex justify-between items-start ${language === 'ar' ? 'flex-row-reverse' : ''}`}>
          <div>
            <h1 className="text-2xl font-semibold">{t('financialAccounting')}</h1>
            <p className="text-green-100 mt-1">
              {t('financialDescription')}
            </p>
          </div>
          <div className={`hidden md:flex items-center gap-3 ${language === 'ar' ? 'flex-row-reverse' : ''}`}>
            <button className="px-4 py-2 bg-white/10 backdrop-blur-sm rounded-lg border border-white/20 hover:bg-white/20 transition-all duration-300 text-white text-sm font-medium flex items-center gap-2 whitespace-nowrap">
              <RefreshCw className="h-4 w-4" />
              {t('refresh')}
            </button>
            <button 
              onClick={() => setShowNewEntryModal(true)}
              className="px-4 py-2 bg-white text-green-600 rounded-lg text-sm font-medium hover:bg-green-50 transition-all duration-300 flex items-center gap-2 whitespace-nowrap"
            >
              <Plus className="h-4 w-4 flex-shrink-0" />
              <span>{t('newEntry')}</span>
            </button>
          </div>
        </div>

        {/* Centered Tabs */}
        <div className="mt-6 flex justify-center">
          <div className={`inline-flex bg-white/10 rounded-lg p-1 backdrop-blur-sm overflow-x-auto ${
            language === 'ar' ? 'space-x-reverse space-x-1' : 'space-x-1'
          }`}>
            {mainTabs.map((tab) => {
              const Icon = tab.icon;
              return (
                <button
                  key={tab.id}
                  onClick={() => {
                    setActiveTab(tab.id);
                    // Don't hide side menu when switching tabs
                  }}
                  className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                    language === 'ar' ? 'flex-row-reverse' : ''
                  } ${
                    activeTab === tab.id
                      ? 'bg-white text-green-700'
                      : 'text-white/90 hover:bg-white/10'
                  }`}
                >
                  <Icon className="w-4 h-4" />
                  {tab.label}
                </button>
              );
            })}
          </div>
        </div>
      </div>

      {/* Quick Stats - Separate from Header */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {quickStats.map((stat, index) => (
          <motion.div
            key={index}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: index * 0.1 }}
            className={`bg-white rounded-xl shadow-sm border border-gray-200 p-5 hover:shadow-md transition-shadow ${language === 'ar' ? 'text-right' : 'text-left'}`}
          >
            <div className={`flex items-start justify-between ${language === 'ar' ? 'flex-row-reverse' : ''}`}>
              <div className="flex-1">
                <p className="text-sm font-medium text-gray-600 mb-1">{stat.label}</p>
                <p className="text-2xl font-bold text-gray-900">{stat.value}</p>
                <div className={`flex items-center gap-2 mt-2 ${language === 'ar' ? 'flex-row-reverse' : ''}`}>
                  <span className={`text-sm font-medium ${stat.trend === 'up' ? 'text-green-600' : 'text-red-600'}`}>
                    {stat.change}
                  </span>
                  <span className="text-xs text-gray-500">{language === 'ar' ? 'مقارنة بالشهر الماضي' : 'vs last month'}</span>
                </div>
              </div>
              <div className={`p-3 rounded-lg ${stat.bgColor}`}>
                <stat.icon className={`h-6 w-6 ${stat.color}`} />
              </div>
            </div>
          </motion.div>
        ))}
      </div>

        {/* Tab Content */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200">
          <div className="p-6">
            <AnimatePresence mode="wait">
              <motion.div
                key={activeTab}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
                transition={{ duration: 0.2 }}
              >
                {activeTab === 'dashboard' && <DashboardTab />}
                {activeTab === 'general-ledger' && <GeneralLedgerTab />}
                {activeTab === 'accounts-payable' && <AccountsPayableTab />}
                {activeTab === 'accounts-receivable' && <AccountsReceivableTab />}
                {activeTab === 'reconciliation' && <ReconciliationTab />}
                {activeTab === 'tax-zatca' && <TaxZATCATab />}
                {activeTab === 'asset-management' && <AssetManagementTab />}
                {activeTab === 'posting-rules' && <PostingRulesTab />}
                {activeTab === 'closing-audit' && <ClosingAuditTab />}
                {activeTab === 'settings' && <SettingsTab />}
              </motion.div>
            </AnimatePresence>
          </div>
        </div>
      </div>

      {/* New Entry Modal */}
      <AnimatePresence>
        {showNewEntryModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4"
            onClick={() => setShowNewEntryModal(false)}
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              onClick={(e) => e.stopPropagation()}
              className="bg-white rounded-xl shadow-2xl max-w-2xl w-full p-6"
            >
              <div className={`flex items-center justify-between mb-6 ${language === 'ar' ? 'flex-row-reverse' : ''}`}>
                <h2 className="text-2xl font-bold text-gray-900">{t('newEntry')}</h2>
                <button onClick={() => setShowNewEntryModal(false)} className="p-2 hover:bg-gray-100 rounded-lg">
                  <X className="h-5 w-5 text-gray-600" />
                </button>
              </div>
              <div className={`grid grid-cols-2 gap-4 ${language === 'ar' ? 'text-right' : 'text-left'}`}>
                <button
                  onClick={() => {
                    setShowNewEntryModal(false);
                    setActiveTab('general-ledger');
                    // Trigger journal entry form in GeneralLedgerTab
                    setTimeout(() => {
                      const event = new CustomEvent('openJournalEntryForm');
                      window.dispatchEvent(event);
                    }, 100);
                  }}
                  className={`p-6 border-2 border-gray-200 rounded-lg hover:border-green-500 hover:bg-green-50 transition-all ${language === 'ar' ? 'text-right' : 'text-left'}`}
                >
                  <BookOpen className="h-8 w-8 text-green-600 mb-3" />
                  <h3 className="font-semibold text-gray-900 mb-1">{t('journalEntries')}</h3>
                  <p className="text-sm text-gray-600">{t('newJournalEntry')}</p>
                </button>
                <button
                  onClick={() => {
                    setShowNewEntryModal(false);
                    setActiveTab('accounts-payable');
                    setTimeout(() => {
                      const event = new CustomEvent('openPaymentForm');
                      window.dispatchEvent(event);
                    }, 100);
                  }}
                  className={`p-6 border-2 border-gray-200 rounded-lg hover:border-blue-500 hover:bg-blue-50 transition-all ${language === 'ar' ? 'text-right' : 'text-left'}`}
                >
                  <CreditCard className="h-8 w-8 text-blue-600 mb-3" />
                  <h3 className="font-semibold text-gray-900 mb-1">{t('recordPayment')}</h3>
                  <p className="text-sm text-gray-600">{t('recordPayment')}</p>
                </button>
                <button
                  onClick={() => {
                    setShowNewEntryModal(false);
                    setActiveTab('accounts-receivable');
                    setTimeout(() => {
                      const event = new CustomEvent('openReceiptForm');
                      window.dispatchEvent(event);
                    }, 100);
                  }}
                  className={`p-6 border-2 border-gray-200 rounded-lg hover:border-green-500 hover:bg-green-50 transition-all ${language === 'ar' ? 'text-right' : 'text-left'}`}
                >
                  <Receipt className="h-8 w-8 text-green-600 mb-3" />
                  <h3 className="font-semibold text-gray-900 mb-1">{t('recordReceipt')}</h3>
                  <p className="text-sm text-gray-600">{t('recordReceipt')}</p>
                </button>
                <button
                  onClick={() => {
                    setShowNewEntryModal(false);
                    setActiveTab('asset-management');
                    setTimeout(() => {
                      const event = new CustomEvent('openAssetForm');
                      window.dispatchEvent(event);
                    }, 100);
                  }}
                  className={`p-6 border-2 border-gray-200 rounded-lg hover:border-purple-500 hover:bg-purple-50 transition-all ${language === 'ar' ? 'text-right' : 'text-left'}`}
                >
                  <Building2 className="h-8 w-8 text-purple-600 mb-3" />
                  <h3 className="font-semibold text-gray-900 mb-1">{t('addAsset')}</h3>
                  <p className="text-sm text-gray-600">{t('addAsset')}</p>
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

const FinancialDashboard = () => {
  return (
    <LanguageProvider>
      <FinancialDashboardContent />
    </LanguageProvider>
  );
};

export default FinancialDashboard;
